/**
 * SystemHealthWidget — Live status grid for all ABET services
 * Shows: Receiver, HF Space, 5 monitors, API credits, VM stats
 * Data source: abet-metrics-collector CF Worker /status endpoint
 * Refresh: 30s
 * CSS prefix: shw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchMetricsStatus, fetchHealth } from '../lib/api'
import { Activity, Server, Cpu, HardDrive, Wifi, Key, RefreshCw } from 'lucide-react'
import './SystemHealthWidget.css'

function formatUptime(seconds) {
  if (!seconds || seconds <= 0) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function StatusDot({ status }) {
  const color = status === 'ok' || status === true || status === 1 ? 'green'
    : status === 'warning' ? 'yellow'
    : status === 'error' || status === false || status === 0 ? 'red'
    : 'gray'
  return <div className={`shw-status-dot ${color}`} />
}

function ServiceCard({ title, icon: Icon, status, metrics = [], uptime, error }) {
  return (
    <div className="shw-card">
      <div className="shw-card-header">
        <StatusDot status={status} />
        {Icon && <Icon size={14} color="#94a3b8" />}
        <span className="shw-card-title">{title}</span>
      </div>
      <div className="shw-metrics">
        {metrics.map((m, i) => (
          <div key={i} className="shw-metric">{m.label}: <span>{m.value}</span></div>
        ))}
      </div>
      {uptime && <div className="shw-uptime">Uptime: {uptime}</div>}
      {error && <div className="shw-error">{error}</div>}
    </div>
  )
}

export default function SystemHealthWidget() {
  const [snapshot, setSnapshot] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastCheck, setLastCheck] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)

    // Try metrics collector first, fall back to direct health check
    const metricsRes = await fetchMetricsStatus()

    if (metricsRes.status === 'ok' && metricsRes.data?.snapshot) {
      setSnapshot(metricsRes.data.snapshot)
      setError(null)
    } else {
      // Fallback: direct health check
      const healthRes = await fetchHealth()
      if (healthRes.status === 'ok') {
        setSnapshot({ mlb_monitors: { hf_space_running: { value: 1 }, ...healthRes.data } })
        setError(null)
      } else {
        setError(metricsRes.message || 'Failed to fetch metrics')
      }
    }

    setLoading(false)
    setLastCheck(new Date().toLocaleTimeString())
  }, [])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 30000)
    return () => clearInterval(id)
  }, [fetchData])

  if (loading && !snapshot) return <div className="shw-loading">Loading system health...</div>

  const oe = snapshot?.odds_engine || {}
  const mlb = snapshot?.mlb_monitors || {}
  const api = snapshot?.theoddsapi || {}
  const vm = snapshot?.vm || {}

  return (
    <div className="shw-container">
      <div className="shw-header">
        <h3><Activity size={16} /> System Health</h3>
        <span className="shw-refresh">
          <RefreshCw size={12} /> {lastCheck || '...'}
        </span>
      </div>

      {error && <div className="shw-error">{error}</div>}

      <div className="shw-grid">
        <ServiceCard
          title="Odds Receiver"
          icon={Server}
          status={oe.receiver_up?.value}
          metrics={[
            { label: 'Books', value: `${oe.active_book_count?.value ?? '?'}/${oe.total_book_count?.value ?? '?'}` },
            { label: 'Rate', value: oe.ingest_rate_per_min?.value != null ? `${oe.ingest_rate_per_min.value.toFixed(1)}/min` : '—' },
            { label: 'WS', value: oe.ws_connections?.value ?? '—' },
          ]}
          uptime={formatUptime(oe.receiver_uptime_s?.value)}
        />

        <ServiceCard
          title="HF Space (MLB Monitors)"
          icon={Cpu}
          status={mlb.hf_space_running?.value}
          metrics={[
            { label: 'Monitors', value: `${mlb.monitors_alive?.value ?? '?'}/5` },
            { label: 'Crashes', value: mlb.crashes_today?.value ?? '—' },
            { label: 'Events/hr', value: mlb.events_per_hour?.value != null ? Math.round(mlb.events_per_hour.value) : '—' },
          ]}
          uptime={formatUptime(mlb.coordinator_uptime_s?.value)}
        />

        <ServiceCard
          title="TheOddsAPI Credits"
          icon={Key}
          status={api.api_credits_pct?.value > 90 ? 'warning' : api.api_credits_pct?.value != null ? 'ok' : 'gray'}
          metrics={[
            { label: 'Usage', value: api.api_credits_pct?.value != null ? `${api.api_credits_pct.value}%` : '—' },
            { label: 'Used', value: api.api_credits_used?.value ?? '—' },
            { label: 'Capacity', value: api.api_credits_capacity?.value ?? '—' },
          ]}
        />

        <ServiceCard
          title="Oracle VM"
          icon={HardDrive}
          status={vm.disk_usage_pct?.value > 85 ? 'warning' : vm.disk_usage_pct?.value != null ? 'ok' : 'gray'}
          metrics={[
            { label: 'Disk', value: vm.disk_usage_pct?.value != null ? `${vm.disk_usage_pct.value.toFixed(1)}%` : '—' },
            { label: 'RAM', value: vm.memory_usage_pct?.value != null ? `${vm.memory_usage_pct.value.toFixed(1)}%` : '—' },
            { label: 'CPU', value: vm.cpu_usage_pct?.value != null ? `${vm.cpu_usage_pct.value.toFixed(1)}%` : '—' },
          ]}
        />
      </div>
    </div>
  )
}
